import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  Bars3Icon,
  ExclamationTriangleIcon,
  ArrowsUpDownIcon,
  UserGroupIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';
import { clampUsernamePrefixInput, exampleUsernamesFromPrefix, USERNAME_PREFIX_HINT } from '../utils/usernameFromPrefix';

// Available task modules
const TASK_MODULES = [
  { id: 'privacy_check', label: 'Privacy Check', description: 'Unprotect account if protected' },
  { id: 'change_username', label: 'Change Username', description: 'Set a new unique @username from a name prefix' },
  { id: 'dob_phone_check', label: 'Username | DoB | Phone', description: 'Change username (optional), verify password, check/add DoB & phone' },
  { id: 'dob_email_check', label: 'Username | DoB | Email', description: 'Change username (optional), verify password, check/add DoB (no phone requirement) — marks account as email-verified' },
  { id: 'update_bio', label: 'Bio Update', description: 'Update name, bio, profile picture, banner' },
  { id: 'fetch_username', label: 'Fetch Username', description: 'Fetch current username from X' },
  { id: 'extract_profile', label: 'Extract Profile', description: 'Extract full profile data' },
];

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  running: 'bg-blue-500',
  paused: 'bg-yellow-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500',
};

const STEP_STATUS_COLORS = {
  pending: 'text-gray-400',
  running: 'text-blue-500 animate-pulse',
  completed: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-gray-400',
};

const ACCOUNT_STATUS_LABELS = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  partial_failure: 'Partial',
  failed: 'Failed',
  skipped: 'Skipped',
  captcha: 'Captcha',
  auth_expired: 'Auth Expired',
  suspended: 'Suspended',
};

const BIO_SUB_LABELS = {
  nameBio: 'Name / bio',
  profilePicture: 'Profile picture',
  banner: 'Banner'
};

const BioUpdateSubSteps = ({ progress }) => {
  if (!progress || typeof progress !== 'object') return null;
  const keys = ['nameBio', 'profilePicture', 'banner'];
  const rows = keys.filter(k => progress[k] !== null && progress[k] !== undefined);
  if (rows.length === 0) return null;
  return (
    <div className="ml-6 mt-1.5 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-600 pl-3">
      {rows.map(key => {
        const ok = progress[key] === true;
        return (
          <div key={key} className="flex items-center gap-2 text-xs">
            <span className={ok ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
              {ok ? '✓' : '✗'} {BIO_SUB_LABELS[key] || key}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const accountHasFailedStep = (entry) => Array.isArray(entry.steps) && entry.steps.some(s => s.status === 'failed');

// ─────────────────────────────────────────────
// Logs Modal
// ─────────────────────────────────────────────
const LogsModal = ({ flow, onClose, onRetryQueued }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      const res = await api.get(`/account-update-flow/flows/${flow._id}/logs`);
      setLogs(res.data || []);
    } catch (e) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [flow._id]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const failedCount = logs.filter((entry) =>
    accountHasFailedStep(entry) || ['failed', 'partial_failure', 'captcha', 'auth_expired', 'suspended'].includes(entry.status)
  ).length;

  const queueRetry = async (accountIds, { autoStart = true } = {}) => {
    setRetrying(true);
    try {
      const res = await api.post(`/account-update-flow/flows/${flow._id}/retry-failed`, {
        accountIds: accountIds && accountIds.length ? accountIds : undefined,
        autoStart
      });
      const queued = res.data?.queued;
      toast.success(
        autoStart
          ? `Retrying ${queued != null ? queued : 'failed'} account(s) — flow started`
          : 'Retry queued for failed accounts — press Start'
      );
      onRetryQueued?.(res.data);
      await loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to queue retries');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Execution Logs</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{flow.name}</p>
            {failedCount > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Retry re-runs failed steps only when possible (bio: failed name/bio, avatar, or banner). Starts automatically; stops the flow first if it is still running.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {failedCount > 0 && (
              <button
                type="button"
                disabled={retrying}
                onClick={() => queueRetry(null)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium"
              >
                <ArrowPathIcon className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
                Retry all failed ({failedCount})
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No execution data yet</div>
          ) : (
            logs.map((entry, idx) => {
              const acct = entry.accountId;
              const username = acct?.username || entry.accountId;
              const accountIdForRetry = acct?._id || entry.accountId;
              const isExpanded = expandedAccount === idx;
              const showRetryBtn = accountHasFailedStep(entry)
                || ['failed', 'partial_failure', 'captcha', 'auth_expired', 'suspended'].includes(entry.status);
              const statusColor = entry.status === 'completed' ? 'text-green-500' :
                entry.status === 'failed' || entry.status === 'captcha' || entry.status === 'auth_expired' || entry.status === 'suspended' ? 'text-red-500' :
                entry.status === 'partial_failure' ? 'text-yellow-500' :
                entry.status === 'running' ? 'text-blue-500' : 'text-gray-400';

              return (
                <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      onClick={() => setExpandedAccount(isExpanded ? null : idx)}
                      className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition text-left min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-sm font-medium flex-shrink-0 ${statusColor}`}>
                          {entry.status === 'completed' ? <CheckCircleIcon className="h-5 w-5 inline" /> :
                           entry.status === 'failed' || entry.status === 'captcha' || entry.status === 'auth_expired' ? <XCircleIcon className="h-5 w-5 inline" /> :
                           entry.status === 'running' ? <ArrowPathIcon className="h-5 w-5 inline animate-spin" /> :
                           <ClockIcon className="h-5 w-5 inline" />}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">@{username}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          entry.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          entry.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          entry.status === 'partial_failure' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          entry.status === 'captcha' || entry.status === 'auth_expired' || entry.status === 'suspended' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {ACCOUNT_STATUS_LABELS[entry.status] || entry.status}
                        </span>
                      </div>
                      {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                    </button>
                    {showRetryBtn && (
                      <button
                        type="button"
                        disabled={retrying}
                        onClick={(e) => {
                          e.stopPropagation();
                          const id = typeof accountIdForRetry === 'object' && accountIdForRetry?._id
                            ? accountIdForRetry._id
                            : accountIdForRetry;
                          queueRetry(id ? [id] : []);
                        }}
                        className="px-3 py-2 border-l border-gray-200 dark:border-gray-600 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50 whitespace-nowrap"
                      >
                        Retry failed
                      </button>
                    )}
                  </div>
                  {isExpanded && entry.steps && (
                    <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-gray-700">
                      {entry.error && (
                        <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs">
                          {entry.error}
                        </div>
                      )}
                      <div className="mt-3 space-y-1.5">
                        {entry.steps.map((step, si) => (
                          <div key={si}>
                            <div className="flex flex-wrap items-center gap-3 py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/40">
                              <span className={`${STEP_STATUS_COLORS[step.status]}`}>
                                {step.status === 'completed' ? <CheckCircleIcon className="h-4 w-4" /> :
                                 step.status === 'failed' ? <XCircleIcon className="h-4 w-4" /> :
                                 step.status === 'running' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> :
                                 step.status === 'skipped' ? <ExclamationTriangleIcon className="h-4 w-4" /> :
                                 <ClockIcon className="h-4 w-4" />}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium min-w-[140px]">
                                {TASK_MODULES.find(m => m.id === step.taskType)?.label || step.taskType}
                              </span>
                              <span className={`text-xs ${STEP_STATUS_COLORS[step.status]}`}>{step.status}</span>
                              {step.error && (
                                <span className="text-xs text-red-500 dark:text-red-400 truncate max-w-[300px]" title={step.error}>
                                  {step.error}
                                </span>
                              )}
                            </div>
                            {step.taskType === 'update_bio' && step.result?.bioUpdateProgress && (
                              <BioUpdateSubSteps progress={step.result.bioUpdateProgress} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Flow Card
// ─────────────────────────────────────────────
const FlowCard = ({ flow, onStart, onStop, onDelete, onViewLogs, onEdit, onRetryFailed, onConcurrencyChange, retryingId }) => {
  const progress = flow.progress || {};
  const total = progress.total || 0;
  const failed = progress.failed || 0;
  const done = (progress.completed || 0) + failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const canRetryFailed = failed > 0 && flow.status !== 'draft';
  const isRetrying = retryingId === flow._id;
  const [editingConcurrency, setEditingConcurrency] = useState(false);
  const [concurrencyDraft, setConcurrencyDraft] = useState(flow.concurrency || 1);
  const [savingConcurrency, setSavingConcurrency] = useState(false);

  useEffect(() => {
    if (!editingConcurrency) setConcurrencyDraft(flow.concurrency || 1);
  }, [flow.concurrency, editingConcurrency]);

  const saveConcurrency = async () => {
    const n = Math.max(1, Math.min(parseInt(concurrencyDraft, 10) || 1, 50));
    if (n === (flow.concurrency || 1)) {
      setEditingConcurrency(false);
      return;
    }
    setSavingConcurrency(true);
    try {
      await onConcurrencyChange?.(flow._id, n);
      setEditingConcurrency(false);
    } finally {
      setSavingConcurrency(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{flow.name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {flow.modules?.filter(m => m.enabled).map(m =>
                TASK_MODULES.find(t => t.id === m.taskType)?.label || m.taskType
              ).join(' → ')}
            </p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full text-white font-medium ${STATUS_COLORS[flow.status] || 'bg-gray-500'}`}>
            {flow.status}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>{progress.completed || 0} completed, {failed} failed</span>
            <span>{done}/{total} ({pct}%)</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                flow.status === 'completed' ? 'bg-green-500' :
                flow.status === 'failed' ? 'bg-red-500' :
                'bg-blue-500'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
          <UserGroupIcon className="h-4 w-4" />
          <span>{total} accounts</span>
          <span className="mx-1">|</span>
          {!editingConcurrency ? (
            <button
              type="button"
              onClick={() => {
                setConcurrencyDraft(flow.concurrency || 1);
                setEditingConcurrency(true);
              }}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium"
              title="Change concurrency (applies on next Start / Retry failed)"
            >
              Concurrency: {flow.concurrency || 1}
              <PencilSquareIcon className="h-3.5 w-3.5 opacity-70" />
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span>Concurrency:</span>
              <input
                type="number"
                min={1}
                max={50}
                value={concurrencyDraft}
                onChange={(e) => setConcurrencyDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveConcurrency();
                  if (e.key === 'Escape') setEditingConcurrency(false);
                }}
                className="w-16 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                autoFocus
                disabled={savingConcurrency}
              />
              <button
                type="button"
                onClick={saveConcurrency}
                disabled={savingConcurrency}
                className="px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium"
              >
                {savingConcurrency ? '…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditingConcurrency(false)}
                disabled={savingConcurrency}
                className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs"
              >
                Cancel
              </button>
            </span>
          )}
          {flow.randomizeOrder && (
            <>
              <span className="mx-1">|</span>
              <ArrowsUpDownIcon className="h-3.5 w-3.5" />
              <span>Randomized</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(flow.status === 'draft' || flow.status === 'paused' || flow.status === 'completed' || flow.status === 'failed') && (
            <button
              onClick={() => onStart(flow._id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition"
            >
              <PlayIcon className="h-4 w-4" /> Start
            </button>
          )}
          {flow.status === 'running' && (
            <button
              onClick={() => onStop(flow._id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium transition"
            >
              <StopIcon className="h-4 w-4" /> Stop
            </button>
          )}
          {canRetryFailed && (
            <button
              type="button"
              disabled={isRetrying}
              onClick={() => onRetryFailed?.(flow)}
              title="Re-queue failed accounts and start the flow (stops first if still running)"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium transition"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying…' : `Retry failed (${failed})`}
            </button>
          )}
          <button
            onClick={() => onViewLogs(flow)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium transition"
          >
            <DocumentTextIcon className="h-4 w-4" /> Logs
          </button>
          {flow.status !== 'running' && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(flow._id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60 text-indigo-800 dark:text-indigo-200 text-sm font-medium transition"
            >
              <PencilSquareIcon className="h-4 w-4" /> Edit
            </button>
          )}
          {flow.status !== 'running' && (
            <button
              onClick={() => onDelete(flow._id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 text-sm font-medium transition"
            >
              <TrashIcon className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Create / Edit Flow Modal
// ─────────────────────────────────────────────
const FlowFormModal = ({ mode = 'create', initialFlow = null, onClose, onSaved, accounts, models, serviceUrls }) => {
  const [name, setName] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [enabledModules, setEnabledModules] = useState(
    TASK_MODULES.map((m, i) => ({ taskType: m.id, enabled: true, order: i, settings: {} }))
  );
  const [bioSource, setBioSource] = useState('manual');
  const [bioModelId, setBioModelId] = useState('');
  const [bioManual, setBioManual] = useState({
    names: '', descriptions: '', updateName: false, updateDescription: false,
    updateProfilePicture: false, updateBannerImage: false
  });
  const [profilePicFiles, setProfilePicFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [randomizeOrder, setRandomizeOrder] = useState(false);
  const [concurrency, setConcurrency] = useState(5);
  const [delayStepsMin, setDelayStepsMin] = useState(3);
  const [delayStepsMax, setDelayStepsMax] = useState(8);
  const [delayAccountsMin, setDelayAccountsMin] = useState(5);
  const [delayAccountsMax, setDelayAccountsMax] = useState(15);
  const [creating, setCreating] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [step, setStep] = useState(1); // 1 = modules, 2 = bio config, 3 = accounts, 4 = settings

  // Drag state for modules
  const [dragIdx, setDragIdx] = useState(null);

  const isEdit = mode === 'edit' && initialFlow?._id;

  useEffect(() => {
    if (!isEdit || !initialFlow) return;
    setName(initialFlow.name || '');
    const ids = (initialFlow.accountIds || []).map(a => (typeof a === 'object' && a._id ? a._id : a));
    setSelectedAccounts(ids);
    const fromFlow = initialFlow.modules || [];
    const merged = TASK_MODULES.map((m, i) => {
      const fm = fromFlow.find(x => x.taskType === m.id);
      return {
        taskType: m.id,
        enabled: fm ? fm.enabled : false,
        order: fm != null ? fm.order : i,
        settings: (() => {
          const s = { ...(fm?.settings || {}) };
          if (s.usernameNamePrefix) s.usernameNamePrefix = clampUsernamePrefixInput(s.usernameNamePrefix);
          return s;
        })()
      };
    });
    merged.sort((a, b) => a.order - b.order);
    setEnabledModules(merged);
    setBioSource(initialFlow.bioSource || 'manual');
    setBioModelId(initialFlow.bioModelId?._id || initialFlow.bioModelId || '');
    const bm = initialFlow.bioManual || {};
    setBioManual({
      names: Array.isArray(bm.names) ? bm.names.join('\n') : (bm.names || ''),
      descriptions: Array.isArray(bm.descriptions) ? bm.descriptions.join('\n') : (bm.descriptions || ''),
      updateName: bm.updateName === true,
      updateDescription: bm.updateDescription === true,
      updateProfilePicture: bm.updateProfilePicture === true,
      updateBannerImage: bm.updateBannerImage === true
    });
    setProfilePicFiles([]);
    setBannerFiles([]);
    setRandomizeOrder(!!initialFlow.randomizeOrder);
    setConcurrency(initialFlow.concurrency || 5);
    setDelayStepsMin(initialFlow.delayBetweenStepsMin ?? 3);
    setDelayStepsMax(initialFlow.delayBetweenStepsMax ?? 8);
    setDelayAccountsMin(initialFlow.delayBetweenAccountsMin ?? 5);
    setDelayAccountsMax(initialFlow.delayBetweenAccountsMax ?? 15);
    setStep(1);
  }, [isEdit, initialFlow]);

  const hasBioModule = enabledModules.some(m => m.taskType === 'update_bio' && m.enabled);
  const hasUsernameModule = enabledModules.some(m =>
    (m.taskType === 'change_username' || m.taskType === 'dob_phone_check' || m.taskType === 'dob_email_check') && m.enabled
  );
  const requiresUsernamePrefix = enabledModules.some(m => m.taskType === 'change_username' && m.enabled);
  const usernameNamePrefix = enabledModules.find(m => m.taskType === 'change_username')?.settings?.usernameNamePrefix
    || enabledModules.find(m => m.taskType === 'dob_phone_check')?.settings?.usernameNamePrefix
    || enabledModules.find(m => m.taskType === 'dob_email_check')?.settings?.usernameNamePrefix
    || '';

  const updateUsernamePrefix = (value) => {
    const clamped = clampUsernamePrefixInput(value);
    setEnabledModules(prev => prev.map(m =>
      (m.taskType === 'change_username' || m.taskType === 'dob_phone_check' || m.taskType === 'dob_email_check')
        ? { ...m, settings: { ...(m.settings || {}), usernameNamePrefix: clamped } }
        : m
    ));
  };

  const toggleModule = (taskType) => {
    setEnabledModules(prev => prev.map(m =>
      m.taskType === taskType ? { ...m, enabled: !m.enabled } : m
    ));
  };

  const moveModule = (fromIdx, toIdx) => {
    setEnabledModules(prev => {
      const arr = [...prev];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr.map((m, i) => ({ ...m, order: i }));
    });
  };

  // Group accounts
  const grouped = {};
  const ungrouped = [];
  accounts.forEach(a => {
    if (a.group) {
      if (!grouped[a.group]) grouped[a.group] = [];
      grouped[a.group].push(a);
    } else {
      ungrouped.push(a);
    }
  });

  const toggleAccount = (id) => {
    setSelectedAccounts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllAccounts = () => {
    const selectable = accounts.filter(a => a.hasValidSession && a.status === 'active');
    const allSelected = selectable.every(a => selectedAccounts.includes(a._id));
    if (allSelected) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(selectable.map(a => a._id));
    }
  };

  const selectGroup = (groupName) => {
    const groupAccs = (grouped[groupName] || []).filter(a => a.hasValidSession && a.status === 'active');
    const ids = groupAccs.map(a => a._id);
    const allSelected = ids.every(id => selectedAccounts.includes(id));
    if (allSelected) {
      setSelectedAccounts(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedAccounts(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error('Flow name is required');
    if (selectedAccounts.length === 0) return toast.error('Select at least one account');
    const active = enabledModules.filter(m => m.enabled);
    if (active.length === 0) return toast.error('Enable at least one module');
    if (requiresUsernamePrefix && !usernameNamePrefix.trim()) {
      return toast.error('Enter at least one name prefix for Change Username');
    }

    setCreating(true);
    try {
      const hasFiles = profilePicFiles.length > 0 || bannerFiles.length > 0;
      const modules = enabledModules.map(m => {
        const settings = { ...(m.settings || {}) };
        if (settings.usernameNamePrefix) {
          settings.usernameNamePrefix = clampUsernamePrefixInput(settings.usernameNamePrefix);
        }
        return {
          taskType: m.taskType,
          enabled: m.enabled,
          order: m.order,
          settings
        };
      });

      const bioManualPayload = hasBioModule && bioSource === 'manual' ? {
        names: bioManual.names,
        descriptions: bioManual.descriptions,
        updateName: bioManual.updateName,
        updateDescription: bioManual.updateDescription,
        updateProfilePicture: bioManual.updateProfilePicture || profilePicFiles.length > 0,
        updateBannerImage: bioManual.updateBannerImage || bannerFiles.length > 0
      } : {};

      const serviceUrl = serviceUrls?.automation || '';
      const token = localStorage.getItem('token');
      const authHeaders = { Authorization: `Bearer ${token}` };

      if (isEdit) {
        const flowId = initialFlow._id;
        if (hasFiles) {
          const formData = new FormData();
          formData.append('name', name);
          formData.append('accountIds', JSON.stringify(selectedAccounts));
          formData.append('modules', JSON.stringify(modules));
          formData.append('bioSource', bioSource);
          if (bioModelId) formData.append('bioModelId', bioModelId);
          formData.append('bioManual', JSON.stringify(bioManualPayload));
          formData.append('randomizeOrder', String(randomizeOrder));
          formData.append('concurrency', String(concurrency));
          formData.append('delayBetweenStepsMin', String(delayStepsMin));
          formData.append('delayBetweenStepsMax', String(delayStepsMax));
          formData.append('delayBetweenAccountsMin', String(delayAccountsMin));
          formData.append('delayBetweenAccountsMax', String(delayAccountsMax));
          profilePicFiles.forEach(f => formData.append('profilePictures', f));
          bannerFiles.forEach(f => formData.append('bannerImages', f));
          const url = serviceUrl
            ? `${serviceUrl}/api/account-update-flow/flows/${flowId}`
            : `/api/account-update-flow/flows/${flowId}`;
          await axios.patch(url, formData, { timeout: 120000, headers: authHeaders });
        } else {
          await api.patch(`/account-update-flow/flows/${flowId}`, {
            name,
            accountIds: selectedAccounts,
            modules,
            bioSource,
            bioModelId: bioModelId || null,
            bioManual: bioManualPayload,
            randomizeOrder,
            concurrency,
            delayBetweenStepsMin: delayStepsMin,
            delayBetweenStepsMax: delayStepsMax,
            delayBetweenAccountsMin: delayAccountsMin,
            delayBetweenAccountsMax: delayAccountsMax
          });
        }
        toast.success('Flow updated');
      } else if (hasFiles) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('accountIds', JSON.stringify(selectedAccounts));
        formData.append('modules', JSON.stringify(modules));
        formData.append('bioSource', bioSource);
        if (bioModelId) formData.append('bioModelId', bioModelId);
        formData.append('bioManual', JSON.stringify(bioManualPayload));
        formData.append('randomizeOrder', String(randomizeOrder));
        formData.append('concurrency', String(concurrency));
        formData.append('delayBetweenStepsMin', String(delayStepsMin));
        formData.append('delayBetweenStepsMax', String(delayStepsMax));
        formData.append('delayBetweenAccountsMin', String(delayAccountsMin));
        formData.append('delayBetweenAccountsMax', String(delayAccountsMax));

        profilePicFiles.forEach(f => formData.append('profilePictures', f));
        bannerFiles.forEach(f => formData.append('bannerImages', f));

        const url = serviceUrl
          ? `${serviceUrl}/api/account-update-flow/flows`
          : '/api/account-update-flow/flows';

        await axios.post(url, formData, {
          timeout: 60000,
          headers: authHeaders
        });
        toast.success('Flow created');
      } else {
        await api.post('/account-update-flow/flows', {
          name,
          accountIds: selectedAccounts,
          modules,
          bioSource,
          bioModelId: bioModelId || null,
          bioManual: bioManualPayload,
          randomizeOrder,
          concurrency,
          delayBetweenStepsMin: delayStepsMin,
          delayBetweenStepsMax: delayStepsMax,
          delayBetweenAccountsMin: delayAccountsMin,
          delayBetweenAccountsMax: delayAccountsMax
        });
        toast.success('Flow created');
      }

      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || (isEdit ? 'Failed to update flow' : 'Failed to create flow'));
    } finally {
      setCreating(false);
    }
  };

  const totalSteps = hasBioModule ? 4 : 3;
  const adjustedStep = !hasBioModule && step >= 2 ? step + 1 : step;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{isEdit ? 'Edit Update Flow' : 'Create Update Flow'}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Step {step} of {totalSteps}
              {step === 1 && ' — Configure modules'}
              {step === 2 && hasBioModule && ' — Bio update settings'}
              {adjustedStep === 3 && ' — Select accounts'}
              {adjustedStep === 4 && ' — Execution settings'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Flow name (always visible) */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Flow Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Setup accounts for Model Amy"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* Step 1: Modules */}
          {step === 1 && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Drag to reorder. Toggle modules on/off. Use randomize to shuffle order per account.
              </p>
              <div className="space-y-2">
                {enabledModules.map((mod, idx) => {
                  const info = TASK_MODULES.find(t => t.id === mod.taskType);
                  return (
                    <div
                      key={mod.taskType}
                      draggable
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveModule(dragIdx, idx); setDragIdx(null); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition cursor-grab active:cursor-grabbing ${
                        mod.enabled
                          ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60'
                      } ${dragIdx === idx ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      <Bars3Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{info?.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{info?.description}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mod.enabled}
                          onChange={() => toggleModule(mod.taskType)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  );
                })}
              </div>

              {hasUsernameModule && (
                <div className="mt-5 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username name prefixes
                    {requiresUsernamePrefix && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {USERNAME_PREFIX_HINT}
                    {' '}Examples:{' '}
                    <span className="font-mono">amyb4635yo</span>,{' '}
                    <span className="font-mono">amyb4635_yo</span>.
                    {enabledModules.some(m => (m.taskType === 'dob_phone_check' || m.taskType === 'dob_email_check') && m.enabled) && (
                      <> Optional for Username | DoB | Phone and Username | DoB | Email (only changes username when filled).</>
                    )}
                  </p>
                  <textarea
                    value={usernameNamePrefix}
                    onChange={e => updateUsernamePrefix(e.target.value)}
                    placeholder={'amy\namyb\namybl\namyblis\namybliss\namy_bliss'}
                    rows={6}
                    spellCheck={false}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  {exampleUsernamesFromPrefix(usernameNamePrefix).length > 0 && (
                    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      Examples:{' '}
                      {exampleUsernamesFromPrefix(usernameNamePrefix).map((ex, i) => (
                        <span key={ex + i} className="font-mono text-blue-600 dark:text-blue-400 mr-2">@{ex}</span>
                      ))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Bio config (only if bio module enabled) */}
          {step === 2 && hasBioModule && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio Data Source</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBioSource('model')}
                    className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition ${
                      bioSource === 'model'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400'
                    }`}
                  >
                    Select a Model
                  </button>
                  <button
                    onClick={() => setBioSource('manual')}
                    className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition ${
                      bioSource === 'manual'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400'
                    }`}
                  >
                    Manual Input
                  </button>
                </div>
              </div>

              {bioSource === 'model' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Model</label>
                  <select
                    value={bioModelId}
                    onChange={e => setBioModelId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Choose a model...</option>
                    {models.map(m => (
                      <option key={m._id} value={m._id}>
                        {m.name} ({m.nameCount} names, {m.bioCount} bios, {m.profilePictureCount} pics, {m.bannerCount} banners)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bioSource === 'manual' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={bioManual.updateName} onChange={e => setBioManual(p => ({...p, updateName: e.target.checked}))} className="rounded text-blue-600" />
                      Update Display Name
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={bioManual.updateDescription} onChange={e => setBioManual(p => ({...p, updateDescription: e.target.checked}))} className="rounded text-blue-600" />
                      Update Bio
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={bioManual.updateProfilePicture} onChange={e => setBioManual(p => ({...p, updateProfilePicture: e.target.checked}))} className="rounded text-blue-600" />
                      Update Profile Picture
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={bioManual.updateBannerImage} onChange={e => setBioManual(p => ({...p, updateBannerImage: e.target.checked}))} className="rounded text-blue-600" />
                      Update Banner Image
                    </label>
                  </div>

                  {bioManual.updateName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Names (one per line)</label>
                      <textarea
                        value={bioManual.names}
                        onChange={e => setBioManual(p => ({...p, names: e.target.value}))}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        placeholder="AmyBliss&#10;Amy Blissy&#10;A Bliss"
                      />
                    </div>
                  )}
                  {bioManual.updateDescription && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bios (one per line)</label>
                      <textarea
                        value={bioManual.descriptions}
                        onChange={e => setBioManual(p => ({...p, descriptions: e.target.value}))}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        placeholder="Your favorite addiction..."
                      />
                    </div>
                  )}
                  {(bioManual.updateProfilePicture || profilePicFiles.length > 0) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Profile Pictures ({profilePicFiles.length} selected)
                      </label>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={e => setProfilePicFiles(Array.from(e.target.files))}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
                      />
                    </div>
                  )}
                  {(bioManual.updateBannerImage || bannerFiles.length > 0) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Banner Images ({bannerFiles.length} selected)
                      </label>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={e => setBannerFiles(Array.from(e.target.files))}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Accounts */}
          {adjustedStep === 3 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedAccounts.length} selected
                </span>
                <button
                  onClick={selectAllAccounts}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {accounts.filter(a => a.hasValidSession && a.status === 'active').every(a => selectedAccounts.includes(a._id))
                    ? 'Deselect All' : 'Select All Active'}
                </button>
              </div>
              <div className="max-h-[45vh] overflow-y-auto space-y-1 border border-gray-200 dark:border-gray-700 rounded-xl p-2">
                {/* Grouped accounts */}
                {Object.entries(grouped).map(([groupName, groupAccounts]) => {
                  const isExpanded = expandedGroups.has(groupName);
                  const activeInGroup = groupAccounts.filter(a => a.hasValidSession && a.status === 'active');
                  const selectedInGroup = activeInGroup.filter(a => selectedAccounts.includes(a._id));
                  return (
                    <div key={groupName}>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={activeInGroup.length > 0 && selectedInGroup.length === activeInGroup.length}
                          onChange={() => selectGroup(groupName)}
                          className="rounded text-blue-600"
                        />
                        <button onClick={() => {
                          setExpandedGroups(prev => {
                            const n = new Set(prev);
                            n.has(groupName) ? n.delete(groupName) : n.add(groupName);
                            return n;
                          });
                        }} className="flex-1 text-left flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{groupName}</span>
                          <span className="text-xs text-gray-400">({selectedInGroup.length}/{activeInGroup.length})</span>
                          {isExpanded ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400 ml-auto" /> : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 ml-auto" />}
                        </button>
                      </div>
                      {isExpanded && groupAccounts.map(acc => (
                        <AccountRow key={acc._id} acc={acc} selected={selectedAccounts.includes(acc._id)} onToggle={() => toggleAccount(acc._id)} indent />
                      ))}
                    </div>
                  );
                })}
                {/* Ungrouped */}
                {ungrouped.map(acc => (
                  <AccountRow key={acc._id} acc={acc} selected={selectedAccounts.includes(acc._id)} onToggle={() => toggleAccount(acc._id)} />
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Settings */}
          {adjustedStep === 4 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={randomizeOrder} onChange={e => setRandomizeOrder(e.target.checked)} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-300 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Randomize module order per account</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Extract profile will always run after bio update</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Concurrency (parallel accounts)
                </label>
                <input
                  type="number" min={1} max={50} value={concurrency}
                  onChange={e => setConcurrency(parseInt(e.target.value) || 1)}
                  className="w-32 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delay between steps (sec)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} value={delayStepsMin} onChange={e => setDelayStepsMin(parseFloat(e.target.value) || 0)}
                      className="w-20 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    <span className="text-gray-400">-</span>
                    <input type="number" min={0} value={delayStepsMax} onChange={e => setDelayStepsMax(parseFloat(e.target.value) || 0)}
                      className="w-20 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delay between accounts (sec)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} value={delayAccountsMin} onChange={e => setDelayAccountsMin(parseFloat(e.target.value) || 0)}
                      className="w-20 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                    <span className="text-gray-400">-</span>
                    <input type="number" min={0} value={delayAccountsMax} onChange={e => setDelayAccountsMax(parseFloat(e.target.value) || 0)}
                      className="w-20 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition"
          >
            Back
          </button>
          <div className="flex gap-2">
            {step < totalSteps ? (
              <button
                onClick={() => {
                  if (step === 1 && requiresUsernamePrefix && !usernameNamePrefix.trim()) {
                    toast.error('Enter at least one name prefix for Change Username');
                    return;
                  }
                  setStep(s => s + 1);
                }}
                className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={creating}
                className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition disabled:opacity-50"
              >
                {creating ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save changes' : 'Create flow')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountRow = ({ acc, selected, onToggle, indent = false }) => {
  const disabled = !acc.hasValidSession || acc.status !== 'active';
  return (
    <label className={`flex items-center gap-3 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${indent ? 'ml-6' : ''} ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      <input type="checkbox" checked={selected} onChange={onToggle} disabled={disabled} className="rounded text-blue-600" />
      <span className="text-sm text-gray-800 dark:text-gray-200">@{acc.username}</span>
      {acc.lastBioFlowUpdate?.modelName && (
        <span className="text-xs text-gray-400 ml-auto">
          Updated: {acc.lastBioFlowUpdate.modelName} ({new Date(acc.lastBioFlowUpdate.updatedAt).toLocaleDateString()})
        </span>
      )}
      {!acc.hasValidSession && <span className="text-xs text-red-400 ml-auto">No session</span>}
      {acc.status !== 'active' && <span className="text-xs text-yellow-500 ml-auto">{acc.status}</span>}
    </label>
  );
};

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
const UpdateAccounts = () => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [flows, setFlows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flowModal, setFlowModal] = useState(null); // { mode: 'create' } | { mode: 'edit', flow }
  const [logsFlow, setLogsFlow] = useState(null);
  const [serviceUrls, setServiceUrls] = useState({});
  const [retryingFlowId, setRetryingFlowId] = useState(null);

  const loadFlows = useCallback(async () => {
    try {
      const res = await api.get('/account-update-flow/flows');
      setFlows(res.data || []);
    } catch (e) {
      console.error('Error loading flows:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await api.get('/account-update-flow/accounts');
      setAccounts(res.data || []);
    } catch (e) {
      console.error('Error loading accounts:', e);
    }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const res = await api.get('/account-update-flow/models');
      setModels(res.data || []);
    } catch (e) {
      console.error('Error loading models:', e);
    }
  }, []);

  useEffect(() => {
    const loadServiceUrls = async () => {
      try {
        const res = await api.get('/config/services');
        setServiceUrls(res.data || {});
      } catch (_) {}
    };
    loadServiceUrls();
    loadFlows();
    loadAccounts();
    loadModels();
  }, [loadFlows, loadAccounts, loadModels]);

  // Socket real-time updates
  useEffect(() => {
    if (!socket) return;
    socket.emit('join-room', 'account-update-flow-page');

    const handleProgress = (data) => {
      if (data.flowStatus) {
        setFlows(prev => prev.map(f =>
          f._id === data.flowId ? {
            ...f,
            status: data.flowStatus,
            ...(data.progress ? { progress: data.progress } : {}),
            ...(data.concurrency != null ? { concurrency: data.concurrency } : {})
          } : f
        ));
      } else if (data.progress || data.concurrency != null) {
        setFlows(prev => prev.map(f =>
          f._id === data.flowId ? {
            ...f,
            ...(data.progress ? { progress: { ...f.progress, ...data.progress } } : {}),
            ...(data.concurrency != null ? { concurrency: data.concurrency } : {})
          } : f
        ));
      }
    };

    socket.on('account-update-flow-progress', handleProgress);
    return () => {
      socket.emit('leave-room', 'account-update-flow-page');
      socket.off('account-update-flow-progress', handleProgress);
    };
  }, [socket]);

  const handleStart = async (id) => {
    try {
      const res = await api.post(`/account-update-flow/flows/${id}/start`);
      setFlows(prev => prev.map(f => f._id === id ? res.data : f));
      toast.success('Flow started');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to start flow');
    }
  };

  const handleStop = async (id) => {
    try {
      const res = await api.post(`/account-update-flow/flows/${id}/stop`);
      setFlows(prev => prev.map(f => f._id === id ? res.data : f));
      toast.success('Flow stopped');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to stop flow');
    }
  };

  const handleRetryFailed = async (flow) => {
    const failed = flow?.progress?.failed || 0;
    if (!failed) {
      toast.error('No failed accounts to retry');
      return;
    }
    if (!window.confirm(`Retry ${failed} failed account(s) on "${flow.name}"? This will stop the flow if running, re-queue failures, and start again.`)) {
      return;
    }
    setRetryingFlowId(flow._id);
    try {
      const res = await api.post(`/account-update-flow/flows/${flow._id}/retry-failed`, { autoStart: true });
      setFlows(prev => prev.map(f => f._id === flow._id ? res.data : f));
      toast.success(`Retrying ${res.data?.queued ?? failed} failed account(s)`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to retry');
    } finally {
      setRetryingFlowId(null);
    }
  };

  const handleConcurrencyChange = async (id, concurrency) => {
    try {
      const res = await api.patch(`/account-update-flow/flows/${id}/concurrency`, { concurrency });
      setFlows(prev => prev.map(f => f._id === id ? res.data : f));
      toast.success(
        res.data?.status === 'running'
          ? `Concurrency set to ${concurrency} — takes effect on next Start / Retry failed`
          : `Concurrency set to ${concurrency}`
      );
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update concurrency');
      throw e;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this flow?')) return;
    try {
      await api.delete(`/account-update-flow/flows/${id}`);
      setFlows(prev => prev.filter(f => f._id !== id));
      toast.success('Flow deleted');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete flow');
    }
  };

  const openEditFlow = async (id) => {
    try {
      const res = await api.get(`/account-update-flow/flows/${id}`);
      setFlowModal({ mode: 'edit', flow: res.data });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load flow');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Update Accounts</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Run multi-step pipelines to set up accounts — bio, privacy, DoB, and more
            </p>
          </div>
          <button
            onClick={() => setFlowModal({ mode: 'create' })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium shadow-lg shadow-blue-500/25 transition"
          >
            <PlusIcon className="h-5 w-5" />
            New Flow
          </button>
        </div>
      </div>

      {/* Flows list */}
      {loading ? (
        <div className="text-center py-20 text-gray-500">
          <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto mb-3" />
          Loading flows...
        </div>
      ) : flows.length === 0 ? (
        <div className="text-center py-20">
          <ArrowPathIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No flows yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Create a flow to start updating your accounts</p>
            <button
            onClick={() => setFlowModal({ mode: 'create' })}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
          >
            Create your first flow
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {flows.map(flow => (
            <FlowCard
              key={flow._id}
              flow={flow}
              onStart={handleStart}
              onStop={handleStop}
              onDelete={handleDelete}
              onViewLogs={setLogsFlow}
              onEdit={openEditFlow}
              onRetryFailed={handleRetryFailed}
              onConcurrencyChange={handleConcurrencyChange}
              retryingId={retryingFlowId}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      {flowModal && (
        <FlowFormModal
          key={flowModal.mode === 'edit' ? flowModal.flow._id : 'create'}
          mode={flowModal.mode}
          initialFlow={flowModal.mode === 'edit' ? flowModal.flow : null}
          onClose={() => setFlowModal(null)}
          onSaved={loadFlows}
          accounts={accounts}
          models={models}
          serviceUrls={serviceUrls}
        />
      )}

      {/* Logs modal */}
      {logsFlow && (
        <LogsModal
          flow={logsFlow}
          onClose={() => setLogsFlow(null)}
          onRetryQueued={(updated) => {
            loadFlows();
            if (updated) setLogsFlow(updated);
          }}
        />
      )}
    </div>
  );
};

export default UpdateAccounts;
